import { TableCell, TableRow } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useVerificationTabs } from '../useVerificationTabs'

import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import { Table, TimeLeft } from '@percent/admin-dashboard/common/components'
import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import { getDate } from '@percent/admin-dashboard/common/utility'
import { statusMap } from '@percent/admin-dashboard/constants/statusMap'
import { Badge } from '@percent/lemonade'
import styles from './ValidationSubmissionsTable.module.scss'
import { ValidationSubmissionsTableProps } from './ValidationSubmissionsTable.types'
import { ValidationSubmissionsFilters } from './validationSubmissionsFilters/ValidationSubmissionsFilters'
import { TableColumn } from '@percent/admin-dashboard/common/components/table/Table.types'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'

const sortMapTableNamesToProperties = { applicationDate: 'id', timeLeft: 'id' }

export function ValidationSubmissionsTable({
  data,
  isLoading,
  nextPage,
  previousPage,
  totalResults,
  errorMessage,
  queryParams,
  setQueryParams
}: Readonly<ValidationSubmissionsTableProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const verificationTabs = useVerificationTabs()

  const cells: TableColumn[] = [
    { id: 'country', isSortable: false },
    { id: 'organization', isSortable: false },
    { id: 'agentEmail', isSortable: false },
    { id: 'partner', isSortable: false },
    { id: 'status', isSortable: false },
    { id: 'applicationDate', isSortable: true },
    { id: 'timeLeft', isSortable: true }
  ]

  const mapTableHeaderToProperty = (tableHeaderName: string) =>
    Object.keys(sortMapTableNamesToProperties).includes(tableHeaderName)
      ? sortMapTableNamesToProperties[tableHeaderName as keyof typeof sortMapTableNamesToProperties]
      : null

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    setQueryParams({
      direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC',
      sort: mapTableHeaderToProperty(property)
    })
  }

  return (
    <Table
      isLoading={isLoading}
      totalResults={errorMessage ? 0 : totalResults || 0}
      data={data}
      handleRequestSort={handleRequestSort}
      previousPage={previousPage}
      nextPage={nextPage}
      columns={cells}
      {...verificationTabs}
      filtersContent={<ValidationSubmissionsFilters queryParams={queryParams} setQueryParams={setQueryParams} />}
      emptyTableText={t('typography.noSearchResults')}
    >
      {data?.map(row => (
        <TableRow
          className={styles.row}
          key={row.id}
          onClick={handleClickAsLink(`/validation-submissions/${row.id}`, navigate)}
        >
          <TableCell>
            <CountryCodeFlag countryCode={row.organisation.countryCode ?? row.validationRequest.countryCode} />
          </TableCell>
          <TableCell className={styles.shortenColumn}>{row.organisation.name ?? row.validationRequest.name}</TableCell>
          <TableCell className={styles.shortenColumn}>{row.agentVerification.email || t('status.na')}</TableCell>
          <TableCell>{row.partner.name}</TableCell>
          <TableCell>
            <Badge variant={statusMap[row.status].variant} icon={statusMap[row.status].icon}>
              {t(statusMap[row.status].text)}
            </Badge>
          </TableCell>
          <TableCell>{getDate(row.createdAt)}</TableCell>
          <TableCell
            align="right"
            className={styles.rowNoWrap}
            style={{ padding: '16px 50px 16px 0', textAlign: 'left' }}
          >
            <TimeLeft requestedDate={row.status === 'pending' ? row.createdAt : null} />
          </TableCell>
          <TableCell>
            <DropdownSVG className={styles.arrow} />
          </TableCell>
        </TableRow>
      ))}
    </Table>
  )
}
